"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.defaultConfig = void 0;
const prop_types_1 = __importDefault(require("prop-types"));
const react_1 = __importDefault(require("react"));
const get_1 = __importDefault(require("lodash/get"));
const input_1 = __importDefault(require("@kyruus/input"));
const row_1 = __importDefault(require("./row"));
const read_only_1 = __importDefault(require("../read-only"));
const use_form_1 = __importDefault(require("../../form/use-form"));
const text_input_1 = __importDefault(require("../inputs/text-input"));
const styles_1 = require("../../styles");
const simple_select_input_1 = __importDefault(require("../inputs/simple-select-input"));
// Get the read only text for city state zip and country
const getCityStateZipCountry = (address) => {
    const { city, state, zip, country } = address;
    const stateZipCountry = [state, zip, country].filter(Boolean).join(' ');
    return city ? `${city}, ${stateZipCountry}` : stateZipCountry;
};
/**
 * Generate a field JSX element and pass in required props
 */
const generateField = (field, fallbackLabel) => {
    if (!field)
        return null;
    return !field.isVisible ? null : (react_1.default.createElement(text_input_1.default, Object.assign({ label: fallbackLabel }, field)));
};
const formatZip = (zipValue) => {
    // strip out dashes and add a dash back @ 6th character
    let newValue = zipValue.replace(/-/g, '');
    newValue = newValue.replace(/^([0-9]{5})([0-9])/, '$1-$2');
    return newValue;
};
/**
 * Custom zipcode component
 */
const Zipcode = (props) => {
    const { path, label } = props;
    const [zipState, zipActions] = (0, use_form_1.default)(path, {
        eventProcessor: (event) => formatZip(event.target.value),
        type: 'zip'
    });
    const { AddOn, config } = zipState;
    /**
     * Track if we want to show errors so that we can only show errors
     * on blur instead of surfacing the error message while the user is
     * inputting a value
     */
    if (!config.isVisible)
        return null;
    if (config.isReadOnly) {
        return react_1.default.createElement(read_only_1.default, { msg: zipState.value });
    }
    const zipMaxLength = 10;
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(styles_1.FieldFocusWrapper, { hasError: !!zipState.errors.length },
            react_1.default.createElement(input_1.default, { errors: zipState.errors, onChange: zipActions.onChange, onBlur: zipActions.onBlur, value: zipState.value, placeholder: zipState.config.placeholder, disabled: zipState.config.isReadOnly, name: path, "data-testid": path, label: zipState.config.label || label, hideLabel: true, id: path, maxLength: zipMaxLength, displayCharacterCount: zipState.config.displayCharacterCount, characterCountReachedMessage: zipState.config.characterCountReachedMessage, inputMode: "numeric" // for mobile keyboards
                , pattern: "[0-9-]*" // for mobile keyboards
                , autoComplete: zipState.config.autoComplete })),
        react_1.default.createElement(AddOn, null)));
};
Zipcode.propTypes = {
    path: prop_types_1.default.string.isRequired,
    label: prop_types_1.default.string
};
/**
 * Renders rows of connected inputs for the address object
 *
 *         +-----------------------------------------+
 *  label  | street 1                                |
 *         +-----------------------------------------+
 *         | street 2                                |
 *         +-----------------------------------------+
 *         |  city  | state |   zip   |   country    |
 *         +-----------------------------------------+
 */
const AddressInputRow = ({ path, showLabel = true }) => {
    const [{ config: { label, isReadOnly, subFields, width = 'xl', breakpoints, getInitialValue } }] = (0, use_form_1.default)(path);
    const { street1, street2, city, state, zip, country } = subFields;
    if (isReadOnly) {
        const subFieldValues = getInitialValue();
        return (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(row_1.default, { label: showLabel ? label : '', width: width },
                react_1.default.createElement(read_only_1.default, { msg: subFieldValues[(0, get_1.default)(subFields, 'street1.path')] })),
            react_1.default.createElement(row_1.default, { width: width },
                react_1.default.createElement(read_only_1.default, { msg: subFieldValues[(0, get_1.default)(subFields, 'street2.path')] })),
            react_1.default.createElement(row_1.default, { width: width },
                react_1.default.createElement(read_only_1.default, { msg: getCityStateZipCountry({
                        city: subFieldValues[(0, get_1.default)(subFields, 'city.path')],
                        state: subFieldValues[(0, get_1.default)(subFields, 'state.path')],
                        zip: subFieldValues[(0, get_1.default)(subFields, 'zip.path')],
                        country: subFieldValues[(0, get_1.default)(subFields, 'country.path')]
                    }) }))));
    }
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(row_1.default, { width: width, breakpoints: breakpoints, isRequired: street1.isRequired, label: showLabel ? label : '' }, generateField(street1, 'Street 1')),
        react_1.default.createElement(row_1.default, { width: width, breakpoints: breakpoints }, generateField(street2, 'Street 2')),
        react_1.default.createElement(row_1.default, { width: width, breakpoints: breakpoints },
            generateField(city, 'City'),
            state && state.isVisible && (react_1.default.createElement(simple_select_input_1.default, Object.assign({ label: 'State' }, state))),
            zip && zip.isVisible && react_1.default.createElement(Zipcode, Object.assign({ label: 'Zip' }, zip)),
            country && country.isVisible && (react_1.default.createElement(simple_select_input_1.default, Object.assign({ label: 'Country' }, country))))));
};
AddressInputRow.propTypes = {
    path: prop_types_1.default.string.isRequired,
    showLabel: prop_types_1.default.bool
};
exports.default = AddressInputRow;
exports.defaultConfig = {
    street1: { placeholder: 'Street 1', autoComplete: 'address-line1' },
    street2: { placeholder: 'Street 2', autoComplete: 'address-line2' },
    city: { placeholder: 'City', autoComplete: 'address-level2' },
    state: { placeholder: 'State', autoComplete: 'address-level1' },
    zip: {
        placeholder: 'Zip',
        autoComplete: 'postal-code',
        invalidMsg: 'Zip format must be 12345 or 12345-6789'
    },
    country: { placeholder: 'Country', autoComplete: 'country' }
};
//# sourceMappingURL=address-input-row.js.map